import React, { useCallback, useMemo } from "react";
import { uploadAttachments, useUpsertManyReports } from "@/reports/api/upsert";
import { Location } from "@/reports/config/locations";
import { Status } from "@/reports/config/status";
import {
  isManualValveReport,
  isSafetyValveReport,
  ManualValveReport,
  ReportId,
  SafetyValveReport,
} from "@/reports/types/SafetyValveReport";
import {
  assembleAttachmentFormData,
  readSingleUpsertResult,
} from "@/reports/utils/api";
import useBoolean from "@/hooks/useBoolean";
import { message } from "antd";
import { populateASME } from "@/reports/utils/asme";
import { getSuccessMessage, filterNullValues } from "@/reports/utils/report";
import { useQueryClient } from "@tanstack/react-query";
import {
  MANUAL_VALVE_REPORTS_API,
  SAFETY_VALVE_REPORTS_API,
} from "@/reports/config/api";
import { ReportType } from "../types/Api";

interface SaveReportOptions {
  id: ReportId;
  setId: React.Dispatch<React.SetStateAction<ReportId>>;
  location: Location;
  reportType: ReportType;
}
const useSaveReport = ({
  id,
  setId,
  location,
  reportType,
}: SaveReportOptions) => {
  const queryClient = useQueryClient();
  const { mutateAsync: upsertManyReports } = useUpsertManyReports(reportType);
  const { value: isSaved, setValue: setSaved } = useBoolean(true);
  const save = useCallback(
    async (
      values: SafetyValveReport | ManualValveReport,
      status: Status,
      autoSave: boolean,
      noMessage = false
    ) => {
      const commonFields = {
        ...filterNullValues(values),
        status,
        location,
        // these fields are not allowed in the API
        createdAt: undefined,
        updatedAt: undefined,
        createdBy: undefined,
        updatedBy: undefined,
        NewAddedAttachments: undefined,
        DeletedAttachments: undefined,
      };

      let stringifiedReportValues = "";

      if (reportType === "SAFETY_VALVE") {
        stringifiedReportValues = JSON.stringify([
          {
            ...commonFields,
            ...(id.onServer && {
              SafetyValveReportId: id.value,
            }),
            vLift: (values as SafetyValveReport).vLift?.toString(),
            ...populateASME(values as SafetyValveReport),
            ASME: undefined, // specific to SAFETY_VALVE
            Attachments: undefined, // specific to SAFETY_VALVE
          },
        ]);
      } else if (reportType === "MANUAL_VALVE") {
        stringifiedReportValues = JSON.stringify([
          {
            ...commonFields,
            ...(id.onServer && {
              ManualValveRepairReportId: id.value,
            }),
          },
        ]);
      } else {
        throw new Error(`Unknown report type: ${reportType}`);
      }

      const formData = new FormData();

      formData.append("reports", stringifiedReportValues);

      const {
        data: { result: upsertResult },
      } = await upsertManyReports(formData);
      const result = readSingleUpsertResult(upsertResult);

      if (isSafetyValveReport(result) && result.SafetyValveReportId) {
        setId({
          onServer: true,
          value: String(result.SafetyValveReportId),
        });
      } else if (
        isManualValveReport(result) &&
        result.ManualValveRepairReportId
      ) {
        setId({
          onServer: true,
          value: result.ManualValveRepairReportId,
        });
      }

      const { NewAddedAttachments, DeletedAttachments } = values;

      // Assemble form data for attachments if there are any
      if (NewAddedAttachments.length > 0 || DeletedAttachments.length > 0) {
        const attachmentFormData = assembleAttachmentFormData(
          NewAddedAttachments,
          DeletedAttachments
        );
        try {
          let reportId;
          if (isSafetyValveReport(result)) {
            reportId = result.SafetyValveReportId;
          } else if (isManualValveReport(result)) {
            reportId = result.ManualValveRepairReportId;
          }

          if (reportId === undefined || reportId === null) {
            throw new Error("Report ID is missing for attachment upload");
          }

          // Upload attachments to the server
          await uploadAttachments({
            location,
            reportId,
            formData: attachmentFormData,
            reportType,
          });

          // Invalidate the query to refetch the report with the new attachments
          queryClient.invalidateQueries([
            reportType === "SAFETY_VALVE"
              ? SAFETY_VALVE_REPORTS_API
              : MANUAL_VALVE_REPORTS_API,
            "GET /:id",
            id,
          ]);
        } catch (error) {
          message.error("Error uploading attachments");
        }
      }

      setSaved(true);

      if (!noMessage) {
        message.open({
          type: "success",
          content: autoSave ? "Report saved" : getSuccessMessage(status),
        });
      }

      return result;
    },
    [reportType, upsertManyReports, setSaved, id, location, setId, queryClient]
  );
  return useMemo(
    () => ({ isSaved, setSaved, save }),
    [isSaved, setSaved, save]
  );
};
export default useSaveReport;
